import { CheckIcon, PaperClipIcon } from '@heroicons/react/24/outline';
import * as React from 'react';
import { useRef } from 'react';
import { Constants } from '../../../../../Constants';
import { useChat, useFile } from '../../../../../libs/hooks';
import { IAppConfigResult, useAppConfig } from '../../../../../libs/hooks/useAppConfig';
import { ChatMemorySource, DocumentUploadState } from '../../../../../libs/models/ChatMemorySource';
import { useAppDispatch, useAppSelector } from '../../../../../redux/app/hooks';
import { RootState } from '../../../../../redux/app/store';
import {
    setChatMemorySources,
    updateBotResponseStatus,
} from '../../../../../redux/features/conversations/conversationsSlice';
import { formatDateString } from '../../../../utils/DateUtils';
import { getFileIconByFileExtension } from '../../../../utils/DocumentUtils';
import { timestampToDateString } from '../../../../utils/TextUtils';
import { Table } from '../Shared/Table';
import { TabView } from '../TabView';
const EmptyGuid = '00000000-0000-0000-0000-000000000000';

export const DocumentsTab: React.FC = () => {
    const chat = useChat();
    const fileHandler = useFile();
    const { conversations, selectedId } = useAppSelector((state: RootState) => state.conversations);
    const { serviceInfo } = useAppSelector((state: RootState) => state.app);
    const { chatMemorySources } = conversations[selectedId];
    const [importingDocuments, setImportingDocuments] = React.useState(false);
    const [importDocumentCount, setImportDocumentCount] = React.useState(0);
    const localDocumentFileRef = useRef<HTMLInputElement | null>(null);
    const globalDocumentFileRef = useRef<HTMLInputElement | null>(null);
    const dispatch = useAppDispatch();
    const { activeUserInfo } = useAppSelector((state: RootState) => state.app);
    const emailAddress = activeUserInfo?.email ?? '';
    const user = emailAddress;
    const appConfig = useAppConfig();
    const [supportedFileTypes, setSupportedFileTypes] = React.useState<string>('');

    const getSupportedFileTypes = async () => {
        // Fetch dropdown values for default scopes.
        const response: IAppConfigResult<string[]> = await appConfig.getConfigSupportedFileTypes(user);

        if (response.Success && response.Data) {
            if (response.Data.length > 0) setSupportedFileTypes(`,${response.Data.join(',')}`);
        }
    };

    React.useEffect(() => {
        getSupportedFileTypes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        if (chatMemorySources && chatMemorySources.length > 0) {
            const inProgressCount = chatMemorySources.filter(
                (x) => x.documentUploadState === DocumentUploadState.InProgress,
            ).length;

            if (inProgressCount > 0) {
                const batchSize = Math.max(importDocumentCount, inProgressCount);
                if (inProgressCount > importDocumentCount) {
                    setImportDocumentCount(inProgressCount);
                }

                setImportingDocuments(true);
                dispatch(
                    updateBotResponseStatus({
                        chatId: selectedId,
                        status: batchSize > 1 ? `Uploading documents` : `Uploading document`,
                    }),
                );
            } else {
                setImportingDocuments(false);
                dispatch(updateBotResponseStatus({ chatId: selectedId, status: undefined }));
                setImportDocumentCount(0);
                // Only refresh memory sources if not already up-to-date, no imports in progress,
                const hasExcelWithEmptyAgentFileId = chatMemorySources.some(
                    (x) => x.name.toLowerCase().endsWith('.xlsx') && (!x.agentFileId || x.agentFileId.trim() === ''),
                );
                if (importDocumentCount === 0 && hasExcelWithEmptyAgentFileId) {
                    void chat.getChatMemorySources(selectedId).then((sources) => {
                        if (JSON.stringify(chatMemorySources) !== JSON.stringify(sources)) {
                            dispatch(setChatMemorySources({ chatId: selectedId, chatMemorySources: sources }));
                        }
                    });
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatMemorySources, selectedId, importDocumentCount]);

    const { rows } = useTable(chatMemorySources ?? []);

    const headings = ['Name', 'Created on', 'Size', 'Access', 'Progress'];
    if (serviceInfo.isInsightFeatureEnabled) {
        headings.splice(4, 0, 'Expires on');
    }

    return (
        <TabView title="Documents" showDescription={rows.length === 0}>
            <div className="items-center">
                {/* Hidden input for file upload. Only accept .txt and .pdf files for now. */}
                <input
                    type="file"
                    ref={localDocumentFileRef}
                    style={{ display: 'none' }}
                    accept={`${Constants.app.importTypes}${supportedFileTypes}`}
                    multiple={true}
                    onChange={() => {
                        void fileHandler.handleImport(selectedId, localDocumentFileRef, false).finally(() => {
                            void chat.getChatMemorySources(selectedId).then((sources) => {
                                dispatch(setChatMemorySources({ chatId: selectedId, chatMemorySources: sources }));
                            });
                        });
                    }}
                />
                <input
                    type="file"
                    ref={globalDocumentFileRef}
                    style={{ display: 'none' }}
                    accept={`${Constants.app.importTypes}${supportedFileTypes}`}
                    multiple={true}
                    onChange={() => {
                        void fileHandler.handleImport(selectedId, globalDocumentFileRef, true).finally(() => {
                            void chat.getChatMemorySources(selectedId).then((sources) => {
                                dispatch(setChatMemorySources({ chatId: selectedId, chatMemorySources: sources }));
                            });
                        });
                    }}
                    disabled={true}
                />
                <button
                    disabled={
                        conversations[selectedId].disabled || conversations[selectedId].hidden || importingDocuments
                    }
                    onClick={() => localDocumentFileRef.current?.click()}
                    type="button"
                    className={`${conversations[selectedId].disabled || conversations[selectedId].hidden || importingDocuments ? 'inline-flex shadow mb-3.5 mr-4 justify-center rounded-lg p-2 bg-primary-300 cursor-not-allowed opacity-50' : 'inline-flex shadow cursor-pointer text-slate-50 justify-center mb-3.5 rounded-lg p-2 bg-primary-300 hover:bg-primary-700 dark:text-slate-50 transition-all duration-200'}`}>
                    <span className="sr-only">Attach file</span>
                    <PaperClipIcon className="h-5 w-5" strokeWidth={1.5} />
                    <span className="px-2 text-sm">Upload a document</span>
                </button>
                {/* {importingDocuments && (
                    <div
                        className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-solid border-primary border-e-transparent align-[-0.125em] text-surface motion-reduce:animate-[spin_1.5s_linear_infinite] dark:text-white"
                        role="status"></div>
                )} */}
            </div>

            {serviceInfo.isInsightFeatureEnabled && rows && rows.length > 0 && (
                <div>
                    Documents marked with an expiration date have a limited window of time for which you can ask
                    questions about the content. Once this period has passed you will not be able to ask further
                    questions of these documents. Any previous answers will be shown in the chat by default regardless
                    of the expiration dates.
                </div>
            )}
            {rows && rows.length > 0 && (
                <div className="mb-3.5 overflow-hidden">
                    <Table
                        data={{
                            headings: headings,
                            rows: rows.map((item) => ({
                                id: item.id,
                                type: 'tr',
                                checked: false, // Assuming no initial selection
                                name: renderNameCell(item),
                                createdOn: item.createdOn.label,
                                size: renderFileSizeCell(item),
                                access: item.access,
                                ...(serviceInfo.isInsightFeatureEnabled && { expiresOn: item.expiresOn.label }),
                                progress: renderProgressCell(item),
                            })),
                            hidden: ['type', 'checkbox', 'checked', 'id'],
                        }}
                        isSelectAllEnabled={false}
                    />
                </div>
            )}
        </TabView>
    );
};

function useTable(resources: ChatMemorySource[]) {
    const items = resources.map((item) => ({
        id: item.id,
        checked: false, // Assuming no initial selection
        type: 'tr',
        name: {
            label: item.name,
            icon: getFileIconByFileExtension(item.name),
            url: item.hyperlink,
        },
        createdOn: {
            label: timestampToDateString(item.createdOn),
            timestamp: item.createdOn,
        },
        expiresOn: {
            label:
                item.documentRetentionDate && item.agentFileId ? formatDateString(item.documentRetentionDate) : 'N/A',
            timestamp: item.documentRetentionDate,
        },
        size: item.size,
        access: getAccessString(item.chatId),
        documentUploadState: item.documentUploadState,
        chatId: item.chatId,
    }));

    return { rows: items };
}

function renderNameCell(item) {
    return (
        <div key={item.id} className="flex">
            <div className="[&>span]:flex [&>span]:h-6 [&>span]:w-6 h-6 w-6 mr-2 sm:mt-1">{item.name.icon}</div>
            <a href={item.name.url} title={item.name.label} className="sm:max-w-80 xl:max-w-100 truncate">
                {item.name.label}
            </a>
        </div>
    );
}

function renderFileSizeCell(item) {
    return (
        <div key={`${item.id}-tokens`}>
            {item.documentUploadState === DocumentUploadState.InProgress ? 'N/A' : humanFileSize(item.size)}
        </div>
    );
}

function renderProgressCell(item) {
    return (
        <div key={`${item.id}-progress`}>
            {item.documentUploadState === DocumentUploadState.InProgress ? (
                <div
                    className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-solid border-primary border-e-transparent align-[-0.125em] text-surface motion-reduce:animate-[spin_1.5s_linear_infinite] dark:text-white"
                    role="status"></div>
            ) : (
                <CheckIcon className="h-6 w-6 text-green-500 items-center" />
            )}
        </div>
    );
}

function getAccessString(chatId: string) {
    return chatId === EmptyGuid ? 'Global' : 'This chat';
}

function humanFileSize(size) {
    const i = size == 0 ? 0 : Math.floor(Math.log(size) / Math.log(1024));
    return +(size / Math.pow(1024, i)).toFixed(2) * 1 + ' ' + ['B', 'kB', 'MB', 'GB', 'TB'][i];
}
